import React, { useState, useEffect, useContext, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { FileText, Clock, CheckCircle } from "lucide-react";
import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";
import AlertModal from "../../components/AlertModal";
import { SkeletonStyles, SkeletonBox, LoadingPill } from "../../components/SkeletonLoader";

import text from "./Constant/DocumentRequests.translations";
import getResponsiveStyles from "./Style/DocumentRequests.styles";
import useDocumentRequests from "./hooks/useDocumentRequests";
import PendingRequestsTab from "./components/PendingRequestsTab";
import CompletedRequestsTab from "./components/CompletedRequestsTab";
import ProcessModal from "./components/ProcessModal";
import DuplicateConfirmModal from "./components/DuplicateConfirmModal";

const DocumentRequestsPage = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const t = text[language];

  // ─── Active Tab ────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("pending");

  // ─── Screen Size ──────────────────────────────────────────────────────────
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    isMobile: window.innerWidth <= 768,
    isTablet: window.innerWidth > 768 && window.innerWidth <= 1024,
    isDesktop: window.innerWidth > 1024,
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenSize({
        width,
        isMobile: width <= 768,
        isTablet: width > 768 && width <= 1024,
        isDesktop: width > 1024,
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ─── Alert Modal ───────────────────────────────────────────────────────────
  const [alert, setAlert] = useState({
    open: false,
    title: "",
    message: "",
    variant: "info",
    showCancel: false,
    onConfirm: null,
  });

  const showAlert = (
    title,
    message,
    variant = "info",
    showCancel = false,
    onConfirm = null
  ) => {
    setAlert({ open: true, title, message, variant, showCancel, onConfirm });
  };

  const closeAlert = () => setAlert((prev) => ({ ...prev, open: false }));

  // ─── Hook ──────────────────────────────────────────────────────────────────
  const {
    pendingRequests,
    completedRequests,
    isLoading,
    selectedRequest,
    setSelectedRequest,
    showProcessModal,
    setShowProcessModal,
    comments,
    setComments,
    showDuplicateConfirm,
    duplicateExistingDoc,
    pendingApproveId,
    canApproveDocuments,
    canRejectDocuments,
    canDownloadDocuments,
    canViewDocuments,
    canProcessDocuments,
    handleApprove,
    doApprove,
    handleReject,
    handleDownload,
    handlePreview,
    dismissDuplicateConfirm,
  } = useDocumentRequests(language, showAlert);

  // ─── Styles ────────────────────────────────────────────────────────────────
  const styles = getResponsiveStyles(screenSize, theme, language, isLoading);

  // ─── Skeleton فقط في أول تحميل — مش هيظهر تاني مع كل ريفريش ─────────────────
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => {
    if (!isLoading) setPageReady(true);
  }, [isLoading]);

  // ─── Notification deep-link (?documentId=...) — switch to the right tab
  // and open that request's process modal.
  const [searchParams] = useSearchParams();
  const focusConsumedRef = useRef(false);
  useEffect(() => {
    const focusId = searchParams.get("documentId");
    if (!focusId || focusConsumedRef.current) return;
    const inPending = pendingRequests.find((r) => r.id === Number(focusId));
    const inCompleted = completedRequests.find((r) => r.id === Number(focusId));
    const request = inPending || inCompleted;
    if (request) {
      focusConsumedRef.current = true;
      setActiveTab(inPending ? "pending" : "completed");
      setSelectedRequest(request);
      setShowProcessModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRequests, completedRequests, searchParams]);

  // ═══════════════════════════════════════════════════════════
  // INITIAL SKELETON — only until the first successful load
  // ═══════════════════════════════════════════════════════════
  if (!pageReady && isLoading) {
    const isRTL = language === "ar";
    return (
      <div style={styles.container}>
        <SkeletonStyles theme={theme} />
        <div style={styles.card}>
          <LoadingPill
            text={isRTL ? "جارٍ تحميل طلبات المستندات..." : "Loading document requests..."}
            theme={theme}
            isRTL={isRTL}
          />

          {/* Header skeleton */}
          <div style={styles.header}>
            <SkeletonBox width="220px" height="28px" style={{ marginBottom: "10px" }} />
            <SkeletonBox width="300px" height="16px" />
          </div>

          {/* Tabs skeleton */}
          <div style={styles.tabContainer}>
            <SkeletonBox width="140px" height="40px" style={{ borderRadius: "10px 10px 0 0" }} />
            <SkeletonBox width="180px" height="40px" style={{ borderRadius: "10px 10px 0 0" }} />
          </div>

          {/* Request cards skeleton */}
          {[1, 2, 3].map((i) => (
            <div key={i} style={styles.requestCard}>
              <SkeletonBox width="200px" height="20px" style={{ marginBottom: "12px" }} />
              <SkeletonBox width="100%" height="16px" style={{ marginBottom: "8px" }} />
              <SkeletonBox width="70%" height="16px" style={{ marginBottom: "12px" }} />
              <SkeletonBox width="100px" height="28px" style={{ borderRadius: "20px" }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>
            <FileText size={screenSize.isMobile ? 20 : 24} />
            {t.title}
          </h2>
          <p style={styles.subtitle}>{t.subtitle}</p>
        </div>

        {/* Tabs */}
        <div style={styles.tabContainer}>
          <button
            style={styles.tab(activeTab === "pending")}
            onClick={() => setActiveTab("pending")}
          >
            <Clock size={16} />
            {t.pending} ({pendingRequests.length})
          </button>
          <button
            style={styles.tab(activeTab === "completed")}
            onClick={() => setActiveTab("completed")}
          >
            <CheckCircle size={16} />
            {t.completed} - {t.lastMonth} ({completedRequests.length})
          </button>
        </div>

        {/* Pending Tab */}
        {activeTab === "pending" && (
          <PendingRequestsTab
            t={t}
            styles={styles}
            screenSize={screenSize}
            pendingRequests={pendingRequests}
            isLoading={isLoading}
            language={language}
            canProcessDocuments={canProcessDocuments}
            onProcessClick={(request) => {
              setSelectedRequest(request);
              setShowProcessModal(true);
            }}
          />
        )}

        {/* Completed Tab */}
        {activeTab === "completed" && (
          <CompletedRequestsTab
            t={t}
            styles={styles}
            screenSize={screenSize}
            completedRequests={completedRequests}
            isLoading={isLoading}
            language={language}
            canDownloadDocuments={canDownloadDocuments}
            canViewDocuments={canViewDocuments}
            onDownload={handleDownload}
            onPreview={handlePreview}
          />
        )}
      </div>

      {/* Process Modal */}
      {showProcessModal && (
        <ProcessModal
          t={t}
          styles={styles}
          screenSize={screenSize}
          theme={theme}
          isLoading={isLoading}
          selectedRequest={selectedRequest}
          comments={comments}
          setComments={setComments}
          canApproveDocuments={canApproveDocuments}
          canRejectDocuments={canRejectDocuments}
          onApprove={handleApprove}
          onReject={handleReject}
          onClose={() => !isLoading && setShowProcessModal(false)}
        />
      )}

      {/* Duplicate Confirm Modal */}
      {showDuplicateConfirm && (
        <DuplicateConfirmModal
          t={t}
          theme={theme}
          language={language}
          isLoading={isLoading}
          selectedRequest={selectedRequest}
          duplicateExistingDoc={duplicateExistingDoc}
          pendingApproveId={pendingApproveId}
          canViewDocuments={canViewDocuments}
          onApproveReplace={doApprove}
          onDismiss={dismissDuplicateConfirm}
          onPreview={handlePreview}
        />
      )}

      {/* Global Styles */}
      <style>{`
        ::-webkit-scrollbar {
          width: ${screenSize.isMobile ? "4px" : "6px"};
          height: ${screenSize.isMobile ? "4px" : "6px"};
        }
        ::-webkit-scrollbar-track {
          background: ${theme === "light" ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.05)"};
          border-radius: 3px;
        }
        ::-webkit-scrollbar-thumb {
          background: ${theme === "light" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.2)"};
          border-radius: 3px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: ${theme === "light" ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.3)"};
        }
        * { -webkit-tap-highlight-color: transparent; }
        @media (max-width: 768px) {
          * { text-rendering: optimizeSpeed; }
          button { min-height: 44px !important; min-width: 44px !important; }
        }
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
          * { text-rendering: optimizeLegibility; }
        }
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
        *:focus-visible {
          outline: 2px solid ${theme === "dark" ? "#3b82f6" : "#2563eb"};
          outline-offset: 2px;
          border-radius: 4px;
        }
        button:disabled {
          cursor: not-allowed !important;
          opacity: 0.6 !important;
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @media (prefers-contrast: high) {
          button { border: 2px solid currentColor !important; }
        }
        @media print {
          .modal { display: none !important; }
        }
      `}</style>

      {/* Alert Modal */}
      <AlertModal
        open={alert.open}
        title={alert.title}
        message={alert.message}
        variant={alert.variant}
        showCancel={alert.showCancel}
        onConfirm={alert.onConfirm}
        onClose={closeAlert}
        theme={theme}
        language={language}
      />
    </div>
  );
};

export default DocumentRequestsPage;